import React, { useState, useEffect } from 'react';
import { Link, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardList,
  Activity,
  MessageSquare,
  Eye,
  FileText,
  Lightbulb,
  ShoppingCart,
  User as UserIcon,
  FileCheck,
  Calendar,
  Paperclip,
  FileSignature,
  CheckCircle,
  Volume2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getEncounter } from '../utils/encounterApi';
import { Encounter as EncounterType } from '../types/encounter';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { getAuthToken, getAuthHeaders } from '../utils/authUtils';
import { ImageUploadDropZone } from '../components/common/ImageUploadDropZone';
import { useCachedPhotoUrl, useClearPhotoCache } from '../hooks/useCachedPhotoUrl';

// Import section components
import IntakeSection from '../components/encounter/IntakeSection';
import VitalsSection from '../components/encounter/VitalsSection';
import SubjectiveSection from '../components/encounter/SubjectiveSection';
import ObjectiveSection from '../components/encounter/ObjectiveSection';
import AssessmentSection from '../components/encounter/AssessmentSection';
import PlanSection from '../components/encounter/PlanSection';
import OrdersSection from '../components/encounter/OrdersSection';
import BodyMapSection from '../components/encounter/BodyMapSection';
import DischargeInstructionsSection from '../components/encounter/DischargeInstructionsSection';
import FollowUpSection from '../components/encounter/FollowUpSection';
import AttachmentsSection from '../components/encounter/AttachmentsSection';
import ConsentFormsSection from '../components/encounter/ConsentFormsSection';
import SignEncounterSection from '../components/encounter/SignEncounterSection';
import VisitRecordingPlayer from '../components/encounter/VisitRecordingPlayer';

const Encounter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { encounterId, petId } = useParams<{ encounterId: string; petId: string }>();
  const [encounter, setEncounter] = useState<EncounterType | null>(null);
  const [pet, setPet] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasRecording, setHasRecording] = useState(false);
  const lastPathRef = React.useRef<string>('');

  // Use cached photo URL to prevent unnecessary image reloads
  const cachedPhotoUrl = useCachedPhotoUrl(petId || '', pet?.photo_url);
  const clearPhotoCache = useClearPhotoCache(petId || '');

  const encounterSections = [
    { key: 'intake', label: 'Intake', icon: <ClipboardList className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/intake` },
    { key: 'vitals', label: 'Vitals', icon: <Activity className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/vitals` },
    { key: 'subjective', label: 'Subjective', icon: <MessageSquare className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/subjective` },
    { key: 'objective', label: 'Objective', icon: <Eye className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/objective` },
    { key: 'assessment', label: 'Assessment', icon: <FileText className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/assessment` },
    { key: 'plan', label: 'Plan', icon: <Lightbulb className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/plan` },
    { key: 'orders', label: 'Orders', icon: <ShoppingCart className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/orders` },
    { key: 'body-map', label: 'Body Map & Notes', icon: <UserIcon className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/body-map` },
    { key: 'discharge', label: 'Discharge Instructions', icon: <FileCheck className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/discharge` },
    { key: 'follow-up', label: 'Follow Up', icon: <Calendar className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/follow-up` },
    { key: 'attachments', label: 'Attachments', icon: <Paperclip className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/attachments` },
    { key: 'consent', label: 'Consent Forms', icon: <FileSignature className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/consent` },
    { key: 'sign', label: 'Sign Encounter', icon: <CheckCircle className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/sign` },
    ...(hasRecording ? [{ key: 'recording', label: 'Visit Recording', icon: <Volume2 className="w-4 h-4" />, path: `/pet/${petId}/encounter/${encounterId}/recording` }] : []),
  ];

  useEffect(() => {
    if (encounterId && petId) {
      fetchEncounter();
      fetchPet();
    }
  }, [encounterId, petId]);

  const fetchPet = async () => {
    if (!petId) return;

    try {
      const response = await fetch(API_ENDPOINTS.PETS.GET(petId), {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const petData = await response.json();
        setPet(petData);
      }
    } catch (err) {
      console.error('Error fetching pet:', err);
    }
  };

  // Refresh encounter data when switching tabs to ensure fresh data
  useEffect(() => {
    if (encounterId && petId && location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      fetchEncounter();
    }
  }, [location.pathname]);

  const fetchEncounter = async () => {
    if (!encounterId || !petId) return;

    try {
      setLoading(true);
      setError(null);

      // Use the new getEncounter function from encounterApi which takes petId
      const data = await getEncounter(petId, encounterId);
      setEncounter(data);

      // Check if there's a visit recording
      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/v1/encounters/${encounterId}/visit-recording`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        setHasRecording(response.ok);
      } catch {
        setHasRecording(false);
      }
    } catch (err: any) {
      console.error('Failed to fetch encounter:', err);
      setError(err.message || 'Failed to load encounter');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard/appointments');
  };

  const refreshEncounter = async () => {
    await fetchEncounter();
  };

  const handlePhotoUpload = async (file: File) => {
    if (!petId) return;

    try {
      // Step 1: Initiate upload
      const initiateResponse = await fetch(API_ENDPOINTS.PET_PHOTOS.INITIATE_UPLOAD, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pet_id: petId,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size
        })
      });

      if (!initiateResponse.ok) {
        throw new Error('Failed to initiate photo upload');
      }

      const { upload_url, fields, s3_key, file_id } = await initiateResponse.json();

      // Step 2: Upload to S3 using signed URL
      const formData = new FormData();
      if (fields) {
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value as string);
        });
      }
      formData.append('file', file);

      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload photo to S3');
      }

      // Step 3: Complete upload
      const completeResponse = await fetch(API_ENDPOINTS.PET_PHOTOS.COMPLETE_UPLOAD, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pet_id: petId,
          s3_key,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
          file_id
        })
      });

      if (!completeResponse.ok) {
        throw new Error('Failed to complete upload');
      }

      // Clear the photo cache and refresh pet data
      clearPhotoCache();
      await fetchPet();
    } catch (err) {
      console.error('Error uploading photo:', err);
      alert('Failed to upload photo. Please try again.');
    }
  };

  const formatEncounterDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatEncounterTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-dark-bg-primary">
      {/* Encounter Sidebar */}
      <div className="w-64 bg-white dark:bg-dark-bg-secondary border-r border-gray-200 dark:border-dark-border flex flex-col">
        {/* Header Section with Encounter Info */}
        <div className="p-4 border-b border-gray-200 dark:border-dark-border">
          <div className="flex flex-col items-start gap-4 mb-6">
            <ImageUploadDropZone
              onUpload={handlePhotoUpload}
              currentImageUrl={cachedPhotoUrl}
              variant="circle"
              size="xl"
            />
            <div className="text-left">
              <div className="font-bold text-gray-900 dark:text-dark-text-primary">Encounter</div>
              <div className="text-sm text-gray-500 dark:text-dark-text-muted">
                {encounter?.status === 'draft' ? 'Draft' : 'Completed'}
              </div>
            </div>
          </div>

          {/* Encounter Details */}
          {encounter && (
            <div className="space-y-2 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs text-gray-500 dark:text-dark-text-muted flex-shrink-0">Date & Time</div>
                <div className="text-right flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-dark-text-primary text-xs leading-tight">
                    {formatEncounterDate(encounter.created_at)}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-gray-500 dark:text-dark-text-muted flex-shrink-0">Pet</div>
                <Link
                  to={`/pets/${encounter.pet_id}`}
                  className="inline-flex items-center px-3 py-2 text-sm font-semibold border-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-400 dark:border-blue-600 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors cursor-pointer truncate max-w-[140px]"
                  title={`View ${encounter.pet_name || 'pet'} details`}
                >
                  {encounter.pet_name || 'Loading...'}
                </Link>
              </div>
              {pet && pet.species && (
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-gray-500 dark:text-dark-text-muted flex-shrink-0">Type</div>
                  <div className="font-medium text-gray-900 dark:text-dark-text-primary text-right flex-1 min-w-0 truncate">
                    {pet.species}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-gray-500 dark:text-dark-text-muted flex-shrink-0">Owner</div>
                <div className="font-medium text-gray-900 dark:text-dark-text-primary text-right flex-1 min-w-0 truncate">
                  {encounter.owner_name || 'Loading...'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Encounter Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          <div className="space-y-1">
            {encounterSections.map((section) => {
              const isActive = location.pathname === section.path;
              return (
                <Link
                  key={section.key}
                  to={section.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive
                    ? 'bg-green-50 text-green-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {section.icon}
                  <span>{section.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer with Back Button */}
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleBack}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Appointments</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading encounter...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-8">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800">{error}</p>
            </div>
          </div>
        ) : (
          encounter ? (
            <Routes>
              <Route path="intake" element={<IntakeSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route path="vitals" element={<VitalsSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route path="subjective" element={<SubjectiveSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route path="objective" element={<ObjectiveSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route path="assessment" element={<AssessmentSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route path="plan" element={<PlanSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route
                path="orders"
                element={
                  <OrdersSection
                    encounter={encounter}
                    onUpdate={refreshEncounter}
                    petId={petId || ''}
                    petName={pet?.display_name || pet?.name || 'Pet'}
                    petPhotoUrl={cachedPhotoUrl || pet?.photo_url || null}
                  />
                }
              />
              <Route path="body-map" element={<BodyMapSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route path="discharge" element={<DischargeInstructionsSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route path="follow-up" element={<FollowUpSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route path="attachments" element={<AttachmentsSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route path="consent" element={<ConsentFormsSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route path="recording" element={<VisitRecordingPlayer encounterId={encounter.id} />} />
              <Route path="sign" element={<SignEncounterSection encounter={encounter} onUpdate={refreshEncounter} />} />
              <Route index element={<Navigate to="intake" replace />} />
            </Routes>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Encounter not found.</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Encounter;

